// src/components/network/discover/BusinessSuggestions.jsx

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Building, UserPlus } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { db } from '../../../firebase/config';
import { collection, query, where, limit, getDocs, orderBy } from 'firebase/firestore';

const BusinessSuggestions = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const { currentUser } = useAuth();
  const [error, setError] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!currentUser) {
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        // First, get businesses that the current user is already connected to
        const connectionsQuery = query(
          collection(db, 'connections'),
          where('participants', 'array-contains', currentUser.uid),
        );
        
        const connectionsSnapshot = await getDocs(connectionsQuery);
        const connectedBusinessIds = new Set();
        
        connectionsSnapshot.forEach(doc => {
          const data = doc.data();
          // Find the ID of the other participant (not the current user)
          const otherPartyId = data.participants.find(id => id !== currentUser.uid);
          if (otherPartyId) {
            connectedBusinessIds.add(otherPartyId);
          }
        });
        
        // Add current user's ID to exclude from suggestions
        connectedBusinessIds.add(currentUser.uid);
        
        // Get businesses with graceful error handling
        let businessesData = [];
        try {
          // Fetch businesses not already connected
          const businessesQuery = query(
            collection(db, 'businesses'),
            orderBy('name'),
            limit(5)
          );
          
          const businessesSnapshot = await getDocs(businessesQuery);
          
          businessesData = businessesSnapshot.docs
            .map(doc => ({
              id: doc.id,
              ...doc.data()
            }))
            .filter(business => !connectedBusinessIds.has(business.id));
          
        } catch (error) {
          console.log("Error fetching businesses. Using fallback data:", error);
          // Fallback data if businesses can't be fetched
          businessesData = [
            { id: 'sample1', name: 'Tech Solutions Inc.', industry: 'Technology' },
            { id: 'sample2', name: 'Green Logistics', industry: 'Transportation' },
            { id: 'sample3', name: 'Sunrise Healthcare', industry: 'Healthcare' }
          ];
        }
        
        setSuggestions(businessesData.slice(0, 3)); // Limit to 3 suggestions
      } catch (error) {
        console.error('Error fetching business suggestions:', error);
        // Provide fallback suggestions
        setSuggestions([
          { id: 'sample1', name: 'Tech Solutions Inc.', industry: 'Technology' },
          { id: 'sample2', name: 'Green Logistics', industry: 'Transportation' },
          { id: 'sample3', name: 'Sunrise Healthcare', industry: 'Healthcare' }
        ]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSuggestions();
  }, [currentUser]);
  
  if (loading) {
    return (
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-4">Suggested Businesses</h3>
        <div className="animate-pulse space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="flex items-center">
              <div className="w-10 h-10 bg-gray-200 rounded-lg mr-3"></div>
              <div className="space-y-1">
                <div className="h-4 bg-gray-200 rounded w-24"></div>
                <div className="h-3 bg-gray-200 rounded w-16"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div>
      <h3 className="text-lg font-medium text-gray-900 mb-4">Suggested Businesses</h3>
      {suggestions.length === 0 ? (
        <p className="text-gray-500 text-sm">No suggestions available at the moment.</p>
      ) : (
        <div className="space-y-4">
          {suggestions.map(business => (
            <div key={business.id} className="flex items-start">
              <div className="w-10 h-10 rounded-lg bg-gray-100 mr-3 flex-shrink-0 overflow-hidden">
                {business.businessLogoUrl ? (
                  <img 
                    src={business.businessLogoUrl} 
                    alt={business.name} 
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center bg-purple-100 text-[#190192]">
                    <Building size={20} />
                  </div>
                )}
              </div>
              <div>
                <Link 
                  to={`/network/profile/${business.id}`}
                  className="font-medium text-gray-900 hover:text-[#190192]"
                >
                  {business.name}
                </Link>
                <p className="text-gray-500 text-xs">{business.industry || 'Business'}</p>
                <Link
                  to={`/network/profile/${business.id}`}
                  className="mt-1 text-[#190192] text-xs hover:underline inline-flex items-center"
                >
                  <UserPlus size={12} className="mr-1" />
                  Connect
                </Link>
              </div>
            </div>
          ))}
          <Link 
            to="/network/discover"
            className="block text-center text-[#190192] hover:text-[#2C0DB5] text-sm font-medium pt-2"
          >
            View More
          </Link>
        </div>
      )}
    </div>
  );
};

export default BusinessSuggestions;